import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useRef } from "react";

export interface ModalProps {
  open?: boolean;
  children?: React.ReactNode;
  title?: string;
  actionButtons?: React.ReactNode;
  hasCloseButton?: boolean;
  headerButton?: React.ReactNode;
  onClose?: () => void;
}

export function Modal({
  open,
  children,
  title,
  actionButtons,
  hasCloseButton = false,
  headerButton,
  onClose,
}: ModalProps) {
  const actionButtonsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        if (actionButtonsRef.current) {
          const buttons = actionButtonsRef.current.querySelectorAll("button");
          if (buttons.length > 0) {
            const lastButton = buttons[buttons.length - 1];
            lastButton.click();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed z-50 transition-all duration-300 inset-0 bg-black/75 top-0 left-0 right-0 bottom-0 overflow-y-auto scroll-m-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <Dialog.Content className="z-60 modal-content w-[640px] gap-5 top-[15%] duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]">
            <Dialog.Title className="sr-only" />
            <Dialog.Description className="sr-only" />
            <div className="modal-header px-7.5 py-5">
              <h3 className="modal-title">{title}</h3>
              {headerButton && headerButton}
              {hasCloseButton && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-clear shrink-0"
                  data-modal-dismiss="true"
                >
                  <i className="ki-filled ki-cross"></i>
                </button>
              )}
            </div>
            <div className="modal-body grid gap-5 px-7.5 py-2.5">
              {children}
            </div>
            {actionButtons ? (
              <div className="modal-body grid gap-5 px-7.5 pb-7.5 pt-0">
                <div
                  className="flex justify-end gap-2.5"
                  ref={actionButtonsRef}
                >
                  {actionButtons}
                </div>
              </div>
            ) : (
              <div className="pb-2.5" />
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
